// Runtime del modo "Código" del builder: compila y ejecuta la función JS que el
// equipo de analítica (con Claude) escribe por widget para construir la option de
// ECharts a partir de los datos. Es el corazón de la hiperpersonalización a nivel
// componente.
//
// ⚠️ SEGURIDAD: corre JS del analista con `new Function` en el main thread — mismo
// poder que el resto de la app. Es aceptable SOLO porque el builder es admin-only
// e interno, con código generado/revisado vía Claude + Preview. El endurecimiento
// futuro (iframe/Web Worker aislado) implica renderizar ECharts DENTRO del sandbox,
// porque los formatters de la option son funciones vivas que no sobreviven a
// postMessage/serialización.

import {
  buildLineChartWithMarkLine,
  buildMultiLineChart,
  buildHeatmapMatrix,
  buildKPIGauge,
  generateThemeColors,
  axisFormatter,
} from './options';
import { deepMerge } from './deepMerge';
import { formatValue } from '../format';

// Cache de funciones compiladas por string de código (evita recompilar en cada
// re-render; un mismo código → misma función).
const _cache = new Map();

/**
 * Compila el código del widget a una función. El código debe EVALUAR a una
 * función `(data, theme, helpers) => echartsOption` (arrow o `function`).
 * Devuelve { fn, error, errorType }.
 */
export function compileChartFunction(code) {
  if (!code || !code.trim()) {
    return { fn: null, error: 'El código está vacío.', errorType: 'compile' };
  }
  if (_cache.has(code)) return _cache.get(code);

  let result;
  try {
    // Tolera código pegado (de Claude/analista): quita un comentario de línea
    // final y los `;` finales, que romperían el envoltorio `return ( ... )`.
    const expr = code.trim()
      .replace(/\/\/[^\n]*$/, '')
      .replace(/;+\s*$/, '')
      .trim();
    // eslint-disable-next-line no-new-func
    const factory = new Function('return (\n' + expr + '\n);');
    const fn = factory();
    result = typeof fn === 'function'
      ? { fn, error: null, errorType: null }
      : { fn: null, error: 'El código debe evaluar a una función (data, theme, helpers) => option.', errorType: 'compile' };
  } catch (e) {
    result = { fn: null, error: e.message, errorType: 'compile' };
  }

  _cache.set(code, result);
  return result;
}

/** Ejecuta la función ya compilada en try/catch. Devuelve { option, error, errorType }. */
export function executeChartFunction(fn, data, theme, helpers) {
  try {
    const option = fn(data, theme, helpers);
    if (!option || typeof option !== 'object') {
      return { option: null, error: 'La función no devolvió una option de ECharts (objeto).', errorType: 'execute' };
    }
    return { option, error: null, errorType: null };
  } catch (e) {
    return { option: null, error: e.message, errorType: 'execute' };
  }
}

/**
 * Helpers que recibe la función del analista: los MISMOS builders y utilidades
 * que usa el código de la app, para componer o reescribir desde cero.
 */
export function makeHelpers(theme) {
  return {
    buildLineChartWithMarkLine,
    buildMultiLineChart,
    buildHeatmapMatrix,
    buildKPIGauge,
    generateThemeColors,
    axisFormatter,
    deepMerge,
    formatValue,
    theme,
  };
}

/** Atajo: compila + ejecuta. Devuelve { option, error, errorType }. */
export function runChartCode(code, data, theme) {
  const compiled = compileChartFunction(code);
  if (compiled.error) return { option: null, error: compiled.error, errorType: compiled.errorType };
  return executeChartFunction(compiled.fn, data, theme, makeHelpers(theme));
}
